import { useCallback, useMemo } from 'react';
import { ValidationErrors } from '@westwing/appshell_types/loginStateTypes';
import ShopApi, { EXISTING_USER_MESSAGE, NEW_USER_MESSAGE } from '../api/shopApi';
import SalesApi from '../api/salesApi';
import useTranslate from './useTranslate';
import useSignupFormTracking from './useSignupFormTracking';
import { useCustomEvent } from './useCustomEvent';
import { useIsMultiStep } from './useIsMultiStep';
import useIsKnownCustomer from './useIsKnownCustomer';
import { RegisterUserValues, UseApiReturnType } from 'Interfaces/UseApiReturnType';
import { ClubRegistrationStatus, UpdateLoginSateActions } from 'Types/types';
import {
    FORM_TYPES,
    LoginFormValues,
    PasswordResetFormValues,
    SignupFormValues,
} from 'Interfaces/LoginRegisterFormProps';
import { OnFailure, OnFailureData, OnSuccess, OnSuccessData } from 'Interfaces/Api';
import { useConfig } from 'AppShell/hooks';
import isClubMfPath from 'SharedHelpers/isClubMfPath';
import { OverlayType } from 'Interfaces/overlayTracking';

const getIsExistingUser = (status: ClubRegistrationStatus): boolean => status !== ClubRegistrationStatus.WRONG_PASSWORD;

const getIsLoginErrorWrongPassword = (errors: ValidationErrors | {}): boolean =>
    (errors as ValidationErrors)?.email === undefined && !!(errors as ValidationErrors)?.password;

const useApi = (): UseApiReturnType => {
    const { shopApiBaseUrl, buildVersion, currentCountry: country, env, clubBffBaseUrl } = useConfig();
    const { tPlain } = useTranslate();
    const {
        trackLoginErrorWrongPassword,
        trackLoginSuccessFromRegistration,
        trackLoginSuccess,
        trackRegistrationSuccess,
        trackPrivacyPolicyAcceptance,
    } = useSignupFormTracking();
    const { showDoiOverlay, updateUserData, showNotification } = useCustomEvent();
    const isMultiStep = useIsMultiStep();
    const [, setIsKnownCustomer] = useIsKnownCustomer();
    const { showAccountTransferOverlay: onBlocked } = useCustomEvent();
    const shopApi = useMemo(
        () => new ShopApi(shopApiBaseUrl, buildVersion, country),
        [shopApiBaseUrl, buildVersion, country]
    );
    const salesApi = useMemo(() => new SalesApi(country, clubBffBaseUrl, env), [country, clubBffBaseUrl, env]);

    const sendLoginRequest = async (
        formValues: LoginFormValues,
        onSuccess: OnSuccess,
        onFailure: OnFailure,
        fromRegistration: boolean
    ) => {
        if (isClubMfPath()) {
            await salesApi.loginUser(
                formValues,
                () => {
                    trackLoginSuccess(OverlayType.LoginOverlay);
                    onSuccess();
                },
                onFailure
            );
            return;
        }
        const { email, password } = formValues;
        await shopApi.loginUser(email, password, fromRegistration, onSuccess, onFailure, onBlocked);
    };

    const login = async (
        formValues: LoginFormValues,
        fromRegistration: boolean,
        onSuccess: OnSuccess,
        onFailureData: OnFailureData<ValidationErrors | {}>,
        overlay: OverlayType = OverlayType.ShopToClubOverlay
    ) => {
        const handleLoginSuccess = () => {
            setIsKnownCustomer();
            updateUserData(UpdateLoginSateActions.LOGIN);
            trackLoginSuccess(overlay);
            onSuccess();
        };

        const handleLoginFailure = (errors: ValidationErrors | {}) => {
            if (getIsLoginErrorWrongPassword(errors)) {
                trackLoginErrorWrongPassword(overlay);
            }
            onFailureData(errors);
        };
        await sendLoginRequest(formValues, handleLoginSuccess, handleLoginFailure as OnFailure, fromRegistration);
    };

    const showSuccessNotification = (status: ClubRegistrationStatus): void => {
        const isNewUser = status === ClubRegistrationStatus.NEW_REGISTRATION;
        const isDe = country === 'de';
        if (isNewUser && !isDe) {
            showNotification(tPlain(NEW_USER_MESSAGE));
        }

        if (!isNewUser) {
            showNotification(tPlain(EXISTING_USER_MESSAGE));
        }
    };

    const displayDOINewsletter = (status: ClubRegistrationStatus, isClubNewsletterSubscribed = false) => {
        if (!isClubNewsletterSubscribed) {
            return;
        }
        const isNewUser = status === ClubRegistrationStatus.NEW_REGISTRATION;
        const isSubscriptionConfirmed = status !== ClubRegistrationStatus.EXISTING_USER_NOT_SUBSCRIBED;
        // TODO DOI Should be handled as a feature toggle - not bounded to country.
        const shouldShowDoi = country === 'de' && (!isSubscriptionConfirmed || isNewUser);

        if (shouldShowDoi) {
            showDoiOverlay();
        }
    };

    const handleTrackPrivacyPolicyAcceptance = (isNotExistingUser: boolean, isClubNewsletterSubscribed: boolean) => {
        if (isNotExistingUser && isMultiStep) {
            // This is newsletter tracking, but we need to be consistent with bella tracking.
            trackPrivacyPolicyAcceptance(isClubNewsletterSubscribed);
        }
    };

    const onRegisterSuccess =
        (onSuccess: OnSuccess, form: RegisterUserValues, overlay: OverlayType) => (status: ClubRegistrationStatus) => {
            const isExistingUser = getIsExistingUser(status);
            const actionType = isExistingUser
                ? UpdateLoginSateActions.LOGIN
                : UpdateLoginSateActions.EXISTING_USER_LOGIN_FAIL;

            displayDOINewsletter(status, !!form.isClubNewsletterSubscribed);
            showSuccessNotification(status);
            handleTrackPrivacyPolicyAcceptance(!isExistingUser, !!form.isClubNewsletterSubscribed);
            updateUserData(actionType);
            if (status === ClubRegistrationStatus.NEW_REGISTRATION) {
                trackRegistrationSuccess(overlay, !!form.isNewsletterSubscribed);
            } else {
                trackLoginSuccessFromRegistration(overlay);
            }
            setIsKnownCustomer();
            onSuccess();
        };

    const onRegisterFailure =
        (onFailure: OnFailureData<ValidationErrors | {}>, overlay: OverlayType) => (errors: ValidationErrors | {}) => {
            if (getIsLoginErrorWrongPassword(errors)) {
                trackLoginErrorWrongPassword(overlay);
            }
            onFailure(errors);
        };

    const isSharedSignupForm = (raw: SignupFormValues | RegisterUserValues): raw is SignupFormValues =>
        raw.formType === FORM_TYPES.SHARED_SIGNUP_FORM;
    /**
     *  @deprecated: this is contextual and don't belong on such generic interface
     * it should idealy be moved to the component that use the specifec `SignupFormValues`
     */
    const formatAutoSubscribeFormValue = (raw: SignupFormValues | RegisterUserValues): RegisterUserValues => {
        if (!isSharedSignupForm(raw)) {
            return raw;
        }
        const isTermsAccepted = raw.agb === '1';
        const isNewsletterSubscribed = raw.subscribe === '1';
        const registrationValues = {
            email: raw.email,
            password: raw.password,
            isTermsAccepted,
            isNewsletterSubscribed,
            isClubNewsletterSubscribed: false,
        };
        if (!isMultiStep) {
            registrationValues.isNewsletterSubscribed = true;
            registrationValues.isClubNewsletterSubscribed = isTermsAccepted;
        } else {
            registrationValues.isNewsletterSubscribed = isNewsletterSubscribed;
            registrationValues.isClubNewsletterSubscribed = isNewsletterSubscribed;
        }
        return registrationValues;
    };

    const sendRegistrationRequest = async (
        formValues: RegisterUserValues | SignupFormValues,
        onSuccess: OnSuccess,
        onFailureData: OnFailureData<ValidationErrors | {}>,
        overlay: OverlayType
    ) => {
        const formattedValues = formatAutoSubscribeFormValue(formValues);
        if (isClubMfPath()) {
            await salesApi.registerUser(
                formValues as SignupFormValues,
                () => {
                    trackRegistrationSuccess(OverlayType.SignupOverlay, !!formattedValues.isNewsletterSubscribed);
                    onSuccess();
                },
                onFailureData
            );
            return;
        }
        await shopApi.registerNewUserToClub(
            formattedValues,
            onRegisterSuccess(onSuccess, formattedValues, overlay),
            onRegisterFailure(onFailureData, overlay),
            onBlocked
        );
    };

    const registerUser = async (
        formValues: RegisterUserValues,
        onSuccess: OnSuccess,
        onFailureData: OnFailureData<ValidationErrors | {}>,
        overlay: OverlayType = OverlayType.ShopToClubOverlay
    ) => {
        await sendRegistrationRequest(formValues, onSuccess, onFailureData, overlay);
    };

    const getCmsStaticPage = useCallback(
        async (pageKey: string, onSuccessData: OnSuccessData<string>, onFailure: OnFailure) => {
            const onGetPageSuccess = (data: string) => {
                onSuccessData(data);
            };
            const onGetPageFailure = () => {
                onFailure();
            };
            await shopApi.getCmsStaticPage(pageKey, onGetPageSuccess, onGetPageFailure);
        },
        [shopApi]
    );

    const resetPassword = (userData: PasswordResetFormValues, onSuccess: OnSuccess, onFailure: OnFailure) =>
        shopApi.resetPassword(userData, onSuccess, onFailure);

    const sendLoginLink = (userData: PasswordResetFormValues, onSuccess: OnSuccess, onFailure: OnFailure) =>
        shopApi.sendLoginLink(userData, onSuccess, onFailure);

    return {
        registerUser,
        login,
        getCmsStaticPage,
        country,
        resetPassword,
        sendLoginLink,
    };
};

export default useApi;
